import { Link, useLocation } from "react-router-dom";
import { RiSearchLine } from "@remixicon/react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { StoreSwitcher } from "@/components/dashboard/StoreSwitcher";
import { SidebarPlanCard } from "@/components/dashboard/SidebarPlanCard";
import {
  canSeeNavItem,
  isNavItemActive,
  settingsNavItem,
  visibleNavSections,
  type NavItem,
} from "@/components/dashboard/nav-config";
import { useNavAccess } from "@/components/dashboard/use-nav-access";
import { useModifierKeyLabel } from "@/components/dashboard/use-command-menu";
import logo from "@/assets/site_logo_dark.png";

interface SidebarProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenSearch?: () => void;
}

function NavLinkItem({
  item,
  pathname,
  onNavigate,
}: {
  item: NavItem;
  pathname: string;
  onNavigate: () => void;
}) {
  const Icon = item.icon;
  const active = isNavItemActive(item.href, pathname);

  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cn(
        dashboardStyles.navLink,
        active ? dashboardStyles.navActive : dashboardStyles.navIdle,
      )}
    >
      <Icon
        className={cn(
          "h-[18px] w-[18px] shrink-0 transition-colors",
          active ? "text-primary" : "text-muted-foreground group-hover:text-foreground",
        )}
      />
      <span className="truncate">{item.name}</span>
    </Link>
  );
}

function SidebarContent({
  onNavigate,
  onOpenSearch,
}: {
  onNavigate: () => void;
  onOpenSearch?: () => void;
}) {
  const { pathname } = useLocation();
  const access = useNavAccess();
  const sections = visibleNavSections(access);
  const modKey = useModifierKeyLabel();
  const footerItems = [settingsNavItem].filter((item) =>
    canSeeNavItem(item, access),
  );

  return (
    <div className="flex h-full flex-col overflow-y-auto px-3 py-4">
      <Link
        to="/dashboard"
        onClick={onNavigate}
        className="mb-3 hidden items-center px-2.5 md:flex"
      >
        <img src={logo} alt="Sqaleshop" className="brand-mark h-6 w-auto shrink-0 object-contain" />
      </Link>

      <div className="mb-2 hidden md:block">
        <StoreSwitcher />
      </div>

      {onOpenSearch ? (
        <button
          type="button"
          onClick={() => {
            onNavigate();
            onOpenSearch();
          }}
          className="mb-3 flex h-11 w-full items-center gap-2.5 rounded-full border border-border bg-card px-4 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <RiSearchLine className="h-4 w-4  shrink-0" />
          <span className="flex-1 text-left">Search</span>
          <kbd className="hidden rounded-md border border-border bg-muted px-1.5 py-px text-[10px] font-semibold text-muted-foreground md:inline">
            {modKey}K
          </kbd>
        </button>
      ) : null}

      <nav aria-label="Dashboard" className="space-y-4">
        {sections.map((section, index) => (
          <div key={section.label ?? index} className="space-y-0.5">
            {section.label ? (
              <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                {section.label}
              </p>
            ) : null}
            {section.items.map((item) => (
              <NavLinkItem
                key={item.href}
                item={item}
                pathname={pathname}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        ))}
      </nav>

      <div className="mt-auto space-y-3 pt-6">
        <div className="space-y-0.5">
          {footerItems.map((item) => (
            <NavLinkItem
              key={item.href}
              item={item}
              pathname={pathname}
              onNavigate={onNavigate}
            />
          ))}
        </div>
        <SidebarPlanCard onNavigate={onNavigate} />
      </div>
    </div>
  );
}

export function Sidebar({ isOpen, onOpenChange, onOpenSearch }: SidebarProps) {
  const close = () => onOpenChange(false);

  return (
    <>
      <aside
        className={cn(
          "hidden w-[15.5rem] shrink-0 md:block",
          dashboardStyles.sidebar,
        )}
      >
        <SidebarContent onNavigate={close} onOpenSearch={onOpenSearch} />
      </aside>

      <div className="md:hidden">
        <Sheet open={isOpen} onOpenChange={onOpenChange}>
          <SheetContent
            side="left"
            className={cn("w-[18rem] border-0 p-0 pt-10", dashboardStyles.sidebar)}
          >
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SidebarContent onNavigate={close} onOpenSearch={onOpenSearch} />
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
